import { formatDeviceId, type DeviceInfo, type EnrollmentInfo, type GroupInfo, type OrganizationDetail } from '@viewer/protocol';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api, errorText } from '../../lib/api/client';
import { useApi } from '../../lib/api/useApi';
import { Modal } from '../../ui/Modal';
import { StatusDot } from '../../ui/StatusDot';
import { formatRelative } from './labels';
import styles from './org.module.css';

interface Props {
  org: OrganizationDetail;
  canConnect: boolean;
  onConnect(code: string): void;
  onChanged(): Promise<void>;
}

export function DevicesTab({ org, canConnect, onConnect, onChanged }: Props) {
  const devices = useApi<DeviceInfo[]>(`/api/orgs/${org.id}/devices`, 10_000);
  const groups = useApi<GroupInfo[]>(`/api/orgs/${org.id}/groups`);
  const [enrolling, setEnrolling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounced(query, 300);
  const [grouping, setGrouping] = useState<DeviceInfo | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);

  const isOwner = org.role === 'owner';
  const canOrganize = org.role !== 'member';
  const needle = debouncedQuery.trim().toLowerCase();
  const digits = needle.replace(/\D/g, '');
  const visible = devices.data?.filter((device) => {
    if (!needle) return true;
    return (
      device.name.toLowerCase().includes(needle) ||
      (device.groupName?.toLowerCase().includes(needle) ?? false) ||
      formatDeviceId(device.code).toLowerCase().includes(needle) ||
      (digits.length > 0 && device.code.includes(digits))
    );
  });

  async function patch(device: DeviceInfo, body: Record<string, unknown>) {
    setError(null);
    try {
      await api.patch(`/api/orgs/${org.id}/devices/${device.id}`, body);
      await Promise.all([devices.reload(), onChanged()]);
    } catch (err) {
      setError(errorText(err));
    }
  }

  async function unlink(device: DeviceInfo) {
    if (!confirm(`Desvincular "${device.name}" da empresa? Para voltar, será preciso vinculá-lo de novo com um código.`)) return;
    setError(null);
    try {
      await api.delete(`/api/orgs/${org.id}/devices/${device.id}`);
      await Promise.all([devices.reload(), onChanged()]);
    } catch (err) {
      setError(errorText(err));
    }
  }

  function rename(device: DeviceInfo) {
    const name = prompt('Novo nome do computador', device.name)?.trim();
    if (name && name !== device.name) void patch(device, { name });
  }

  function startSelecting() {
    setOpenMenuId(null);
    setSelected(new Set());
    setSelecting(true);
  }

  function stopSelecting() {
    setSelecting(false);
    setSelected(new Set());
    setBulkOpen(false);
  }

  function toggleSelected(id: string) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleVisible() {
    const ids = visible?.map((device) => device.id) ?? [];
    const allOn = ids.length > 0 && ids.every((id) => selected.has(id));
    setSelected((current) => {
      const next = new Set(current);
      for (const id of ids) {
        if (allOn) next.delete(id);
        else next.add(id);
      }
      return next;
    });
  }

  async function moveSelected(groupId: string | null) {
    const pending = (devices.data ?? []).filter((device) => selected.has(device.id) && device.groupId !== groupId);
    if (pending.length > 0) {
      await Promise.all(pending.map((device) => api.patch(`/api/orgs/${org.id}/devices/${device.id}`, { groupId })));
      await Promise.all([devices.reload(), onChanged()]);
    }
    stopSelecting();
  }

  return (
    <section className={styles.panel}>
      <div className={styles.panelHead}>
        <h2 className={styles.panelTitle}>Dispositivos</h2>
        <div className={styles.panelActions}>
          {canOrganize && (devices.data?.length ?? 0) > 0 && !selecting && (
            <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={startSelecting}>
              Alterar grupo
            </button>
          )}
          {isOwner && (
            <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => setEnrolling(true)}>
              + Adicionar dispositivo
            </button>
          )}
        </div>
      </div>
      {org.role === 'member' && <p className={styles.help}>Você vê aqui só os computadores que a empresa liberou para você.</p>}
      <label className={`${styles.field} ${styles.searchField}`}>
        <span className={styles.label} id="device-search-label">
          Buscar dispositivo
        </span>
        <input
          className={styles.input}
          placeholder="Buscar dispositivo..."
          aria-labelledby="device-search-label"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </label>
      {selecting && (
        <div className={styles.bulkBar}>
          <label className={styles.check}>
            <input
              type="checkbox"
              className={styles.rowCheck}
              checked={(visible?.length ?? 0) > 0 && (visible?.every((device) => selected.has(device.id)) ?? false)}
              onChange={toggleVisible}
            />
            {selected.size === 1 ? '1 selecionado' : `${selected.size} selecionados`}
          </label>
          <div className={styles.panelActions}>
            <button type="button" className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSmall}`} onClick={stopSelecting}>
              Cancelar
            </button>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnPrimary} ${styles.btnSmall}`}
              disabled={selected.size === 0}
              onClick={() => setBulkOpen(true)}
            >
              Escolher grupo
            </button>
          </div>
        </div>
      )}
      {error && <p className={styles.error}>{error}</p>}
      {devices.error && <p className={styles.error}>{devices.error}</p>}

      {devices.data?.length === 0 && (
        <p className={styles.empty}>
          {isOwner
            ? 'Nenhum computador vinculado. Clique em Adicionar dispositivo para gerar um código.'
            : 'Nenhum computador disponível para você.'}
        </p>
      )}
      {devices.data && devices.data.length > 0 && visible?.length === 0 && (
        <p className={styles.empty}>Nenhum dispositivo encontrado.</p>
      )}

      <div className={styles.list}>
        {visible?.map((device) => (
          <div key={device.id} className={styles.row}>
            {selecting && (
              <input
                type="checkbox"
                className={styles.rowCheck}
                checked={selected.has(device.id)}
                aria-label={`Selecionar ${device.name}`}
                onChange={() => toggleSelected(device.id)}
              />
            )}
            <div className={styles.rowMain}>
              <span className={styles.rowName}>
                <StatusDot tone={device.online ? 'success' : 'danger'} />
                {device.name}
                {device.unattendedEnabled && (
                  <span
                    className={`${styles.badge} ${styles.badgeAccent}`}
                    title="O cliente liberou o acesso sem supervisão para esta empresa. Só ele pode mudar isso, no próprio computador."
                  >
                    Sem supervisão
                  </span>
                )}
              </span>
              <span className={styles.rowMeta}>
                ID {formatDeviceId(device.code)} · {device.groupName ?? 'Sem grupo'} ·{' '}
                {device.online ? 'online' : `visto ${formatRelative(device.lastSeenAt)}`}
                {device.access && !device.access.permissions.includes('control') && ' · somente visualização'}
              </span>
            </div>
            {!selecting && (
            <DeviceMenu
              device={device}
              open={openMenuId === device.id}
              onToggle={() => setOpenMenuId((current) => (current === device.id ? null : device.id))}
              onClose={() => setOpenMenuId(null)}
              canOrganize={canOrganize}
              isOwner={isOwner}
              canConnect={canConnect && device.online && !!device.access?.permissions.includes('view')}
              showConnect={!!device.access?.permissions.includes('view')}
              onGroup={() => setGrouping(device)}
              onRename={() => rename(device)}
              onUnlink={() => void unlink(device)}
              onConnect={() => onConnect(device.code)}
            />
            )}
          </div>
        ))}
      </div>

      {bulkOpen && (
        <BulkGroupDialog
          count={selected.size}
          groups={groups.data ?? []}
          onClose={() => setBulkOpen(false)}
          onSave={moveSelected}
        />
      )}
      {enrolling && <EnrollDialog org={org} groups={groups.data ?? []} onClose={() => setEnrolling(false)} />}
      {grouping && (
        <AssignGroupDialog
          device={grouping}
          groups={groups.data ?? []}
          onClose={() => setGrouping(null)}
          onSave={async (groupId) => {
            await api.patch(`/api/orgs/${org.id}/devices/${grouping.id}`, { groupId });
            await Promise.all([devices.reload(), onChanged()]);
          }}
        />
      )}
    </section>
  );
}

function DeviceMenu({
  device,
  open,
  onToggle,
  onClose,
  canOrganize,
  isOwner,
  canConnect,
  showConnect,
  onGroup,
  onRename,
  onUnlink,
  onConnect,
}: {
  device: DeviceInfo;
  open: boolean;
  onToggle(): void;
  onClose(): void;
  canOrganize: boolean;
  isOwner: boolean;
  canConnect: boolean;
  showConnect: boolean;
  onGroup(): void;
  onRename(): void;
  onUnlink(): void;
  onConnect(): void;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const hasActions = canOrganize || isOwner || showConnect;

  useEffect(() => {
    if (!open) return;
    function onPointer(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) onClose();
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!hasActions) return null;

  function choose(action: () => void) {
    onClose();
    action();
  }

  return (
    <div className={styles.menuWrap} ref={rootRef}>
      <button
        type="button"
        className={styles.menuButton}
        aria-label={`Ações de ${device.name}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={onToggle}
      >
        <span className={styles.menuDots} aria-hidden>
          <span />
          <span />
          <span />
        </span>
      </button>
      {open && (
        <div className={styles.menu} role="menu">
          {showConnect && (
            <button
              type="button"
              role="menuitem"
              className={styles.menuItem}
              disabled={!canConnect}
              title={device.online ? undefined : 'Computador offline'}
              onClick={() => choose(onConnect)}
            >
              Conectar
            </button>
          )}
          {canOrganize && (
            <button type="button" role="menuitem" className={styles.menuItem} onClick={() => choose(onGroup)}>
              {device.groupName ? `Grupo: ${device.groupName}` : 'Definir grupo'}
            </button>
          )}
          {isOwner && (
            <button type="button" role="menuitem" className={styles.menuItem} onClick={() => choose(onRename)}>
              Renomear
            </button>
          )}
          {isOwner && (
            <button type="button" role="menuitem" className={`${styles.menuItem} ${styles.menuItemDanger}`} onClick={() => choose(onUnlink)}>
              Desvincular
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** Gera o código de vinculação de uso único e mostra como usá-lo. */
function EnrollDialog({ org, groups, onClose }: { org: OrganizationDetail; groups: GroupInfo[]; onClose(): void }) {
  const [groupId, setGroupId] = useState<string>(groups[0]?.id ?? '');
  const [enrollment, setEnrollment] = useState<EnrollmentInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function generate() {
    setBusy(true);
    setError(null);
    try {
      setEnrollment(await api.post<EnrollmentInfo>(`/api/orgs/${org.id}/enrollments`, { groupId: groupId || null }));
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="Adicionar dispositivo" eyebrow={org.name} onClose={onClose}>
      {enrollment ? (
        <div className={styles.form}>
          <p className={styles.help}>
            No computador que vai entrar na empresa, abra o Viewer e clique em <strong>Vincular empresa</strong> no topo
            da janela. Digite este código. Ele vale por 15 minutos e só pode ser usado uma vez.
          </p>
          <p className={styles.code}>{enrollment.code}</p>
          <p className={styles.muted}>
            Vence às {new Date(enrollment.expiresAt).toLocaleTimeString('pt-BR', { timeStyle: 'short' })}.
            {groupId && ` O computador entra no grupo ${groups.find((g) => g.id === groupId)?.name ?? ''}.`}
          </p>
          <div className={styles.actions}>
            <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={() => setEnrollment(null)}>
              Gerar outro
            </button>
            <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={onClose}>
              Concluído
            </button>
          </div>
        </div>
      ) : (
        <div className={styles.form}>
          <p className={styles.help}>
            Não dá para adicionar um computador só pelo ID: alguém precisa digitar um código no próprio computador,
            para garantir que ele é seu.
          </p>
          <label className={styles.field}>
            <span className={styles.label}>Grupo</span>
            <select className={styles.select} value={groupId} onChange={(e) => setGroupId(e.target.value)}>
              <option value="">Sem grupo</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          </label>
          {error && <p className={styles.error}>{error}</p>}
          <div className={styles.actions}>
            <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={onClose} disabled={busy}>
              Cancelar
            </button>
            <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => void generate()} disabled={busy}>
              {busy ? 'Gerando...' : 'Gerar código'}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function BulkGroupDialog({
  count,
  groups,
  onClose,
  onSave,
}: {
  count: number;
  groups: GroupInfo[];
  onClose(): void;
  onSave(groupId: string | null): Promise<void>;
}) {
  const [groupId, setGroupId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await onSave(groupId || null);
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  return (
    <Modal title="Mover para um grupo" onClose={() => { if (!busy) onClose(); }}>
      <form className={styles.form} onSubmit={(e) => void submit(e)}>
        <p className={styles.help}>
          {count === 1
            ? '1 computador selecionado passa para o grupo escolhido.'
            : `${count} computadores selecionados passam para o grupo escolhido.`}
        </p>
        <label className={styles.field}>
          <span className={styles.label}>Grupo</span>
          <select className={styles.select} value={groupId} onChange={(e) => setGroupId(e.target.value)} autoFocus>
            <option value="">Sem grupo</option>
            {groups.map((group) => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
          </select>
        </label>
        {groups.length === 0 && <p className={styles.muted}>Nenhum grupo criado ainda. Crie um na aba Grupos.</p>}
        {error && <p className={styles.error}>{error}</p>}
        <div className={styles.actions}>
          <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={onClose} disabled={busy}>
            Cancelar
          </button>
          <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy}>
            {busy ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function useDebounced(value: string, delayMs: number) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

function AssignGroupDialog({
  device,
  groups,
  onClose,
  onSave,
}: {
  device: DeviceInfo;
  groups: GroupInfo[];
  onClose(): void;
  onSave(groupId: string | null): Promise<void>;
}) {
  const [groupId, setGroupId] = useState(device.groupId ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    const next = groupId || null;
    if (next === device.groupId) {
      onClose();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onSave(next);
      onClose();
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  return (
    <Modal title={`Grupo de ${device.name}`} onClose={() => { if (!busy) onClose(); }}>
      <form className={styles.form} onSubmit={(e) => void submit(e)}>
        <p className={styles.help}>
          Escolha o grupo deste computador. Quem tem acesso ao grupo passa a encontrá-lo na lista.
        </p>
        <label className={styles.field}>
          <span className={styles.label}>Grupo</span>
          <select className={styles.select} value={groupId} onChange={(e) => setGroupId(e.target.value)} autoFocus>
            <option value="">Sem grupo</option>
            {groups.map((group) => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
          </select>
        </label>
        {groups.length === 0 && <p className={styles.muted}>Nenhum grupo criado ainda. Crie um na aba Grupos.</p>}
        {error && <p className={styles.error}>{error}</p>}
        <div className={styles.actions}>
          <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={onClose} disabled={busy}>
            Cancelar
          </button>
          <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy}>
            {busy ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
